import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import _ from "lodash"
import { show, reset } from "./confirmDialogActions"

/**
 * A hook helper to handle dialog confirmation result.
 * When user confirms an action then global state <code>confirmDialog</code> is updated with
 * <code>confirm</code> attribute. Finally this object is suppose to be used to apply the action.
 *
 * @param predicate   : a predicate to check if applyAction should have to be run
 * @param applyAction : function(*): undefined lambda function that will execute the action
 * @param options : dialog box options (title, message)
 * @return a function that allows to open dialog box
 */
export default function useConfirmDialog(predicate, applyAction, options) {
  if (!_.isString(predicate) && !_.isFunction(predicate)) {
    throw new Error(
      "Invalid predicate argument. Expected a string or a function."
    )
  }
  const dispatch = useDispatch()
  const confirmedState = useSelector((state) => state.confirmDialog.confirm)
  const stringifyState = JSON.stringify(confirmedState)

  useEffect(() => {
    if (_.isUndefined(confirmedState)) {
      return () => {
        // delay a bit reset when unmounting parent component
        // it fixes a UX behaviour, which introduces a blink effect
        // when opening modal dialog with default value
        setTimeout(() => dispatch(reset()), 500)
      }
    }

    const shouldApply = _.isString(predicate)
      ? confirmedState[predicate]
      : predicate(confirmedState)
    if (shouldApply) {
      applyAction(confirmedState)
    }

    return () => {
      // delay a bit reset when unmounting parent component
      // it fixes a UX behaviour, which introduces a blink effect
      // when opening modal dialog with default value
      setTimeout(() => dispatch(reset()), 500)
    }
    // voluntary skip others
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifyState])

  /**
   * If no options usage of dialog is made manually via dialog.show API.
   */
  if (!options) {
    return _.noop
  }

  /**
   * Return a runnable function that take current processed data and display dialog box.
   */
  return (data) => {
    const { message, title } = options
    const realMessage = _.isFunction(message) ? message(data) : message
    const toConfirm = { [predicate]: data }
    dispatch(show(realMessage, { title, toConfirm }))
  }
}
